'use client'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription } from '@/components/ui/card'
import { MapPin, Calendar, Clock } from 'react-feather'
import dayjs from 'dayjs'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'

interface PaymentDetails {
  basePrice: number
  finalPrice: number
  paymentReceiver?: string
}

interface OrderData {
  meetingPlace: {
    placeId: AvailabilityMeetingPlace
    description: string
  }
  startDate: string
  speciality: string
  price: number
}

interface PaymentSummaryProps {
  orderData: OrderData
  paymentDetails: PaymentDetails | null
  onPay: () => void
  isProcessing: boolean
}

export function PaymentSummary({ orderData, paymentDetails, onPay, isProcessing }: PaymentSummaryProps) {
  if (!orderData || !paymentDetails) return null

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price)
  }

  const showPriceBreakdown = paymentDetails.basePrice !== paymentDetails.finalPrice

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle><Trans>Order summary</Trans></CardTitle>
          <CardDescription></CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Appointment Details */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar size={16} />
                <span>{dayjs(orderData.startDate).format('dddd D MMMM YYYY')}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Clock size={16} />
                <span>{dayjs(orderData.startDate).format('HH:mm')}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <MapPin size={16} />
                <span>{orderData.meetingPlace.description}</span>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="mt-6 space-y-2">
              {showPriceBreakdown && (
                <div className="flex justify-between text-sm">
                  <span><Trans>Consultation fee</Trans></span>
                  <span>{formatPrice(paymentDetails.basePrice)}</span>
                </div>
              )}
              <div className={showPriceBreakdown ? 'border-t pt-2 flex justify-between font-semibold' : 'flex justify-between font-semibold'}>
                <span><Trans>Total</Trans></span>
                <span>{formatPrice(paymentDetails.finalPrice)}</span>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <button
            data-testid="pay-button"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-300"
            disabled={isProcessing}
            onClick={onPay}
            type="button"
          >
            {isProcessing && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />}
            {t`Pay ${formatPrice(paymentDetails.finalPrice)}`}
          </button>
        </CardFooter>
      </Card>

      {/* Payment Security Notice */}
      <div className="text-center text-sm text-gray-500">
        <p><Trans>You will be redirected to our secure payment provider</Trans></p>
      </div>
    </div>
  )
}
